import { hideToast, showToast } from "app/actions/toast_actions";
import { createContactsRequestDto } from "app/pages/sales-intelligence/modules/company-analysis/helpers/contacts";
import {
    selectActiveProfile,
    selectContactDetails,
    selectContactsCorrectCurrentPage,
    selectContactsTableSearchText,
    selectCurrentCountriesIds,
} from "app/pages/sales-intelligence/modules/company-analysis/store/contacts/selectors";
import trackingService from "app/pages/sales-intelligence/sub-modules/contacts-search/services/tracking/trackingService";
import { multipleContactsWereRevealedAsync } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/action-creators";
import { fetchContactsSearchResultsWithTotalCountThunk } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/effects";
import {
    saveMultipleContactsRevealedThunk,
    showMultipleRevealResultToastThunk,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/store/effects-reveal";
import { selectActionsPanelFilters } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import type { ActionsPanelFilters } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";
import type { TopNRevealRequestDto } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/dto";
import { exportToExcelToastThunk } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/effects";
import { exportContactRevealService } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/services/exportContactRevealService";
import type { PostConfigObject } from "app/services/fetchService";
import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

/**
 * Thunk retrieves and saves contacts based on given `domain` and `panelFilters`.
 * @param domain
 * @param actionsPanelFilters
 */
export function fetchContactsFromActionsPanelThunk(
    domain: string,
    actionsPanelFilters: Partial<ActionsPanelFilters>,
    config?: PostConfigObject,
) {
    return async function fetchContactsFromActionsPanel(dispatch: ThunkDispatchCommon, getState: ThunkGetState) {
        const state = getState();
        const contactDetails = selectContactDetails(state);
        const selectedProfile = selectActiveProfile(state);
        const currentPage = selectContactsCorrectCurrentPage(state);
        const searchText = selectContactsTableSearchText(state);
        const selectedCountriesIds = selectCurrentCountriesIds(state);

        const dto = createContactsRequestDto({
            domain,
            searchText,
            currentPage,
            contactDetails,
            selectedProfile,
            actionsPanelFilters,
            selectedCountriesIds,
        });

        return dispatch(fetchContactsSearchResultsWithTotalCountThunk(dto, config));
    };
}

function revealTopNContactsForDomainThunk(domain: string, n: number) {
    return async function revealTopNContactsForDomain(
        dispatch: ThunkDispatchCommon,
        getState: ThunkGetState,
        deps: ThunkDependencies,
    ) {
        dispatch(multipleContactsWereRevealedAsync.request());

        const { createToast, getToastTitle, getErrorToastTitle, getToastComponent } = exportContactRevealService;
        const toast = createToast(getToastTitle(n));
        dispatch(showToast(getToastComponent(toast.children as string), "success", toast.timestamp));

        try {
            const state = getState();
            const actionsPanelFilters = selectActionsPanelFilters(state);
            const contactDetails = selectContactDetails(state);
            const selectedProfile = selectActiveProfile(state);
            const currentPage = selectContactsCorrectCurrentPage(state);
            const searchText = selectContactsTableSearchText(state);
            const selectedCountriesIds = selectCurrentCountriesIds(state);
            const searchParams = createContactsRequestDto({
                domain,
                searchText,
                currentPage,
                contactDetails,
                selectedProfile,
                actionsPanelFilters,
                selectedCountriesIds,
            });
            const dto: TopNRevealRequestDto = {
                ...searchParams,
                top: n,
            };
            const response = await deps.si.api.contacts.revealTopNContacts(dto);
            let revealedContactsCount = response.uniqueCount;

            if (response.requestId) {
                const statusResponseCount = await deps.si.api.contacts.revealStatus(response.requestId);
                revealedContactsCount += statusResponseCount;
            }

            const resultAction = await dispatch(
                saveMultipleContactsRevealedThunk(
                    response,
                    revealedContactsCount,
                    n,
                    Boolean(state.impersonation.impersonateMode),
                ),
            );

            dispatch(hideToast(toast));

            return { resultAction, toast, response };
        } catch (e) {
            trackingService.trackContactsBulkRevealFailure(n, n);
            dispatch(hideToast(toast));
            dispatch(exportToExcelToastThunk(getErrorToastTitle()));
            return {
                resultAction: dispatch(multipleContactsWereRevealedAsync.failure(e?.message)),
                toast,
                response: undefined,
            };
        }
    };
}

export function revealTopNContactsWithToastThunk(domain: string, n: number) {
    return async function revealTopNContactsWithToast(dispatch: ThunkDispatchCommon) {
        const { resultAction } = await dispatch(revealTopNContactsForDomainThunk(domain, n));

        void dispatch(showMultipleRevealResultToastThunk(resultAction, n));
    };
}
